import _ from 'lodash';
import I18n from '../config/i18n';

export function arePropsMissing(props: unknown[]): boolean {
  return props.some((prop) => typeof prop === 'undefined');
}

export function isLast(index: number, list: unknown[]): boolean {
  return index === list.length - 1;
}

export interface Options {
  label: string;
  value: string | number;
}

export function optionsFromTo(start: number, end: number, translationKey?: string): Options[] {
  return _.range(start, end).map((value) => ({
    value,
    label: translationKey ? I18n.t(translationKey, { count: value }) : `${value}`,
  }));
}

export function getOptionIndex(value: string | number | undefined, options: Options[]): number {
  if (!options) {
    return -1;
  }

  return options.findIndex((option) => {
    if (value == null && (option.value == null || option.value === '')) {
      return true;
    }
    return option.value === value;
  });
}
const isTypeFns =
  'Array Boolean Date Function Integer NaN Null Number Object RegExp String Undefined'
    .split(/\W+/)
    .map((fnName) => (_ as any)[`is${fnName}`].bind(_));

export function isSameType(a: unknown, b: unknown): boolean {
  return isTypeFns.every((isType) => isType(a) === isType(b));
}
/**
 * Turns this:
 *   {a: {b: {c: 5}, d: 6}}
 * Into this:
 *   [[['a', 'b', 'c'], 5], [['a', 'd'], 6]]
 */
export function getObjPath(src: object): any[] {
  const generatePaths = (current: object, path: any[] = [], target: any[] = []): any[] => {
    Object.keys(current).forEach((segment) => {
      const val = (current as any)[segment];
      if (_.isPlainObject(val)) {
        return generatePaths(val, path.concat([segment]), target);
      }
      target.push([path.concat([segment]), val]);
      return target;
    });
    return target;
  };
  return generatePaths(src);
}

/**
 * With (src = {}, value = 5, path = ['a', 'b', 'c']) src afterwards looks like this
 *   {a: {b: {c: 5}}}
 * In case a value already exists at this path, the value is not overwritten
 */
export const createObjPath = (src: any, value: any, path: any[] = []): object => {
  let current = src;
  const lastKey = _.last(path);
  path.forEach((segment) => {
    if (!current[segment]) {
      current[segment] = segment === lastKey ? value : {};
    }
    current = !current[segment] ? {} : current[segment];
  });
  return src;
};

export function noop(): void {
  // nothing
}

export function isTranslationObjectValid(translationObject: any): boolean {
  return (
    translationObject &&
    typeof translationObject === 'object' &&
    Object.keys(translationObject).every(
      (trans) =>
        translationObject[trans] &&
        typeof translationObject[trans] === 'string' &&
        !/missing/.test(translationObject[trans]),
    )
  );
}

export default { noop, arePropsMissing, optionsFromTo, isSameType, getObjPath, createObjPath };
